import React, { useMemo, useState } from 'react';
import { Card, Typography, Input, Space, Row, Col, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import StandardTable from '../../../../components/common/StandardTable';
import { useGetQaqcPipeMaterialcontrol } from '../../../../hooks/genericHooks';
import Button from '../../../../components/common/Button';

// Local row type for material control records
interface QaqcPipeMaterialcontrol {
  id?: number | string;
  section?: string;
  spool_id?: string | number;
  itemcode?: string;
  size_text?: string;
  unit?: string;
  line_no?: string;
  serviceclass_name?: string;
  description?: string | null;
  parts_no?: string | number;
  [key: string]: any;
}

const { Title } = Typography;

const QaqcPipeMaterialControl: React.FC = () => {
  const { data, isLoading } = useGetQaqcPipeMaterialcontrol() as any;
  const list: QaqcPipeMaterialcontrol[] = (data as any)?.data ?? (data as any) ?? [];

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{
    section?: string[];
    spool_id?: string[];
    itemcode?: string[];
    size_text?: string[];
    unit?: string[];
    line_no?: string[];
    serviceclass_name?: string[];
    description?: string[];
  }>({});

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const byFilters = (list as any[]).filter(r => {
      const f = filters;
      const v = (key: keyof QaqcPipeMaterialcontrol) => {
        const val = (r as any)[key];
        return val === null || val === undefined ? '' : String(val);
      };
      if (f.section?.length && !f.section.includes(v('section'))) return false;
      if (f.spool_id?.length && !f.spool_id.includes(v('spool_id'))) return false;
      if (f.itemcode?.length && !f.itemcode.includes(v('itemcode'))) return false;
      if (f.size_text?.length && !f.size_text.includes(v('size_text'))) return false;
      if (f.unit?.length && !f.unit.includes(v('unit'))) return false;
      if (f.line_no?.length && !f.line_no.includes(v('line_no'))) return false;
      if (f.serviceclass_name?.length && !f.serviceclass_name.includes(v('serviceclass_name'))) return false;
      if (f.description?.length && !f.description.some(d => v('description').toLowerCase().includes(String(d).toLowerCase()))) return false;
      return true;
    });
    if (!s) return byFilters;
    const terms = s.split(/\s+/).filter(Boolean);
    return byFilters.filter(r => {
      const hay = `${r.section} ${r.spool_id} ${r.itemcode} ${r.size_text} ${r.unit ?? ''} ${r.line_no} ${r.serviceclass_name} ${r.description ?? ''}`.toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  }, [list, search, filters]);

  const buildOptions = (field: keyof QaqcPipeMaterialcontrol) => {
    // Apply all filters except the current field for cascading effect
    const others: typeof filters = { ...filters, [field]: undefined } as any;
    const filteredForOptions = (list as any[]).filter(r => {
      const v = (k: keyof QaqcPipeMaterialcontrol) => {
        const val = (r as any)[k];
        return val === null || val === undefined ? '' : String(val);
      };
      if (others.section?.length && !others.section.includes(v('section'))) return false;
      if (others.spool_id?.length && !others.spool_id.includes(v('spool_id'))) return false;
      if (others.itemcode?.length && !others.itemcode.includes(v('itemcode'))) return false;
      if (others.size_text?.length && !others.size_text.includes(v('size_text'))) return false;
      if (others.unit?.length && !others.unit.includes(v('unit'))) return false;
      if (others.line_no?.length && !others.line_no.includes(v('line_no'))) return false;
      if (others.serviceclass_name?.length && !others.serviceclass_name.includes(v('serviceclass_name'))) return false;
      if (others.description?.length && !others.description.includes(v('description'))) return false;
      return true;
    });
    const set = new Set<string>();
    filteredForOptions.forEach(r => {
      const val = (r as any)[field];
      const s = val === null || val === undefined ? '' : String(val).trim();
      if (s !== '') set.add(s);
    });
    return Array.from(set).sort((a,b)=>a.localeCompare(b)).map(v=>({ label: v, value: v }));
  };

  const columns: ColumnsType<QaqcPipeMaterialcontrol> = [
    { title: 'Section', dataIndex: 'section', key: 'section', width: 70 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 60 },
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 100 },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 100 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 60 },
    { title: 'Line No', dataIndex: 'line_no', key: 'line_no', width: 120},
    { title: 'S.Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 70 },
    { title: 'Part#', dataIndex: 'parts_no', key: 'parts_no', width: 60 },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 480 },
  ];

  return (
    <Card>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>QA/QC Piping • Material Control</Title>
        <Space>
          <Input
            allowClear
            placeholder="Search section/spool/item/line/desc"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 380 }}
          />
          <Button
            onClick={() => setFilters({})}
            disabled={Object.keys(filters).length === 0}
          >
            Reset Filters
          </Button>
        </Space>
      </Space>

      <Row gutter={[8,8]} style={{ marginBottom: 8 }}>
        <Col xs={12} sm={8} md={6} lg={4} xl={2}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Section"
            options={buildOptions('section')}
            value={filters.section}
            onChange={(v)=>setFilters(prev=>({...prev, section: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={2}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Spool"
            options={buildOptions('spool_id')}
            value={filters.spool_id}
            onChange={(v)=>setFilters(prev=>({...prev, spool_id: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={3}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Item Code"
            options={buildOptions('itemcode')}
            value={filters.itemcode}
            onChange={(v)=>setFilters(prev=>({...prev, itemcode: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={3}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Size"
            options={buildOptions('size_text')}
            value={filters.size_text}
            onChange={(v)=>setFilters(prev=>({...prev, size_text: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={2}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Unit"
            options={buildOptions('unit')}
            value={filters.unit}
            onChange={(v)=>setFilters(prev=>({...prev, unit: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={3}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Line No"
            options={buildOptions('line_no')}
            value={filters.line_no}
            onChange={(v)=>setFilters(prev=>({...prev, line_no: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4} xl={2}>
          <Select
            mode="multiple"
            allowClear
            placeholder="S.Class"
            options={buildOptions('serviceclass_name')}
            value={filters.serviceclass_name}
            onChange={(v)=>setFilters(prev=>({...prev, serviceclass_name: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={12} lg={8} xl={8}>
          <Select
            mode="multiple"
            allowClear
            placeholder="Description"
            options={buildOptions('description')}
            value={filters.description}
            onChange={(v)=>setFilters(prev=>({...prev, description: v?.length? v: undefined}))}
            style={{ width: '100%' }}
          />
        </Col>
      </Row>

      <StandardTable<QaqcPipeMaterialcontrol>
        loading={isLoading}
        dataSource={filtered}
        columns={columns as any}
        rowKey="id"
        bordered
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
      />
    </Card>
  );
};

export default QaqcPipeMaterialControl;
